import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CorsaLogo } from "@/components/Logo";
import { useLang } from "@/i18n";

const pub = (f) => `${process.env.PUBLIC_URL}/${f}`;

// Corsa Exchange brand identity (corsaexchange.ro): deep burgundy #5a1a14 anchor,
// coral #d94b3d CTA, gold #c9921a/#f5d27a accents, warm cream #faf8f5 canvas,
// Playfair Display headings (font-display), Outfit body, JetBrains Mono figures (font-rate).
export default function FinancePage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const f = t.finance;
  const offices = f.offices;

  useEffect(() => {
    window.scrollTo({ top: 0 });
    // Swap the global gold scrollbar for the Corsa burgundy/coral one while this page is mounted.
    document.body.classList.add("corsa-scroll");
    return () => document.body.classList.remove("corsa-scroll");
  }, []);

  const backHome = () => {
    navigate("/");
    setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  return (
    <main className="bg-[#faf8f5]">
      {/* ───────────── HERO — Corsa lockup over fine "blueprint" lines (Taurus/Meliora-style) ───────────── */}
      <section className="relative flex flex-col items-center overflow-hidden bg-gradient-to-br from-[#5a1a14] via-[#521612] to-[#481512] px-6 pb-16 pt-28 text-center sm:pb-24 sm:pt-36">
        {/* fine vertical white hairlines */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 9%)",
          }}
        />
        {/* soft light from the top */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(110%_70%_at_50%_0%,rgba(255,255,255,0.08),transparent_60%)]"
        />
        {/* top scrim so the transparent navbar stays clean over the lines */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#5a1a14] to-transparent" />

        <div className="relative flex flex-col items-center">
          <Reveal delay={0.12}>
            <CorsaLogo className="scale-110 sm:scale-125" />
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mx-auto mt-12 h-px w-14 bg-[#f5d27a]/45 sm:mt-16" />
            <p className="mx-auto mt-6 max-w-xl font-display text-xl italic text-[#f5d27a]/90 sm:mt-8 sm:text-2xl">
              {f.aboutLead}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────── ABOUT — intro copy, figures & CTA ───────────── */}
      <section className="relative overflow-hidden bg-[#481512] py-20 md:py-28">
        <div className="container-luxe">
          <Reveal>
            <div className="mx-auto max-w-2xl space-y-4 text-center font-sans text-sm font-light leading-relaxed text-[#faf8f5]/80 md:text-base">
              <p>{f.p1}</p>
              <p>{f.p2}</p>
            </div>
          </Reveal>

          {/* Mono stat row — the currency-board cue (2×2 on phones, 4-across on desktop) */}
          <Reveal delay={0.1}>
            <div className="mx-auto mt-12 grid max-w-md grid-cols-2 gap-x-10 gap-y-6 sm:max-w-none sm:grid-cols-4">
              {f.stats.map((s) => (
                <div key={s.label} className="flex flex-col items-center">
                  <span className="font-rate text-3xl text-[#faf8f5] md:text-4xl">{s.value}</span>
                  <span className="mt-1.5 font-rate text-[10px] uppercase tracking-[0.22em] text-[#f5d27a]/70">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 text-center">
              <a
                href="https://corsaexchange.ro/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#f5d27a]/45 px-6 py-3 font-rate text-xs uppercase tracking-[0.2em] text-[#faf8f5] transition-colors duration-300 hover:border-[#faf8f5] hover:bg-[#faf8f5] hover:text-[#5a1a14]"
              >
                {f.cta} <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── LOCATIONS — warm cream, alternating editorial list ───────────── */}
      <section className="relative bg-[#faf8f5] pb-16 pt-10 md:pb-24 md:pt-14">
        <div className="container-luxe">
          <Reveal className="mb-12">
            <p className="font-rate mb-3 text-[11px] uppercase tracking-[0.34em] text-[#8b6914]">{f.locationsTitle}</p>
            <h2 className="font-display text-2xl font-light text-[#5a1a14] sm:text-3xl">{f.locationsSubtitle}</h2>
          </Reveal>

          <div className="space-y-14 md:space-y-20">
            {offices.map((o, i) => {
              const flip = i % 2 === 1;
              return (
                <Reveal key={o.img}>
                  <div
                    className={`flex flex-col gap-5 md:flex-row md:items-center md:gap-12 ${
                      flip ? "md:flex-row-reverse" : ""
                    }`}
                  >
                    <div className="md:w-[58%]">
                      <div className="group overflow-hidden rounded-2xl border border-[#5a1a14]/12 shadow-[0_18px_50px_-30px_rgba(90,26,20,0.45)]">
                        <img
                          src={pub(o.img)}
                          alt={o.name}
                          loading="lazy"
                          className="aspect-[16/10] w-full object-cover transition-transform [transition-duration:1400ms] ease-out group-hover:scale-105"
                        />
                      </div>
                    </div>
                    <div className={`md:w-[42%] ${flip ? "md:text-right" : ""}`}>
                      <span className="font-rate text-sm tracking-[0.3em] text-[#c9921a]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className={`mt-3 h-px w-10 bg-[#c9921a]/60 ${flip ? "md:ml-auto" : ""}`} />
                      <h3 className="mt-4 font-display text-2xl text-[#5a1a14] sm:text-3xl">{o.name}</h3>
                      <p
                        className={`mt-3 flex items-start gap-2 font-sans text-sm font-light text-[#5a1a14]/70 md:text-base ${
                          flip ? "md:flex-row-reverse md:text-right" : ""
                        }`}
                      >
                        <MapPin size={16} className="mt-0.5 flex-shrink-0 text-[#d94b3d]" />
                        <span>{o.addr}</span>
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── CONTACT — burgundy band bookend ───────────── */}
      <section id="corsa-contact" className="section-pad relative bg-gradient-to-br from-[#5a1a14] to-[#481512]">
        <div className="container-luxe">
          <Reveal className="mx-auto max-w-xl text-center">
            <p className="font-rate mb-4 text-[11px] uppercase tracking-[0.34em] text-[#f5d27a]">{f.contactTitle}</p>
            <h2 className="font-display text-3xl font-light text-[#faf8f5] sm:text-4xl">{f.hqLabel}</h2>

            <div className="mt-8 flex flex-col items-center gap-4">
              <span className="flex items-center gap-2 font-sans text-sm font-light text-[#faf8f5]/80">
                <MapPin size={16} className="text-[#f5d27a]" /> {offices[0].addr}
              </span>
              <a
                href="tel:0241693219"
                className="flex items-center gap-2 font-rate text-sm text-[#fff5f3]/85 transition-colors hover:text-white"
              >
                <Phone size={16} className="text-[#f5d27a]" /> 0241.693.219
              </a>
              <a
                href="tel:0722641633"
                className="flex items-center gap-2 font-rate text-sm text-[#fff5f3]/85 transition-colors hover:text-white"
              >
                <Phone size={16} className="text-[#f5d27a]" /> 0722.641.633
              </a>
              <a
                href="mailto:office@corsaexchange.ro"
                className="flex items-center gap-2 font-rate text-sm text-[#fff5f3]/85 transition-colors hover:text-white"
              >
                <Mail size={16} className="text-[#f5d27a]" /> office@corsaexchange.ro
              </a>
            </div>

            <button
              onClick={backHome}
              className="mt-12 inline-flex items-center gap-2 rounded-full border border-[#f5d27a]/45 px-6 py-3 font-rate text-xs uppercase tracking-[0.2em] text-[#faf8f5] transition-colors duration-300 hover:border-[#faf8f5] hover:bg-[#faf8f5] hover:text-[#5a1a14]"
            >
              <ArrowLeft size={16} /> {f.back}
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
